import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { STRINGS, isLanguage, legalLanguage, type Language, type LegalLanguage } from './i18n';
import { getDataController, getEffectiveDate, getLastUpdatedDate, getSecurityEmail, getSupportEmail } from './legalInfoUtils';

// Язык страницы — любой из 13 (шапка, подвал, строка-пояснение); сам документ — на английском, русском или испанском (legalLanguage), 2026-10-24.

type PolicySection = {
  title: string;
  content: string;
};

type PolicyTranslations = {
  title: string;
  lastUpdated: string;
  effectiveDate: string;
  dataController: string;
  intro: string;
  sections: PolicySection[];
};

// 2026-10-17 (аудит сторов Б1): политика переписана под рекламу Google AdMob (rewarded), копии сохранения ОС, код и файл сохранения
// и то, что на самом деле оставляет сброс (src/core/Storage.ts resetProgress). Прежняя версия — backlog/store-audit-wrapper-site-2026-10-17/.
// Русский юридический текст — на «вы», как раньше. Раздел «Дети» — вариант по умолчанию (docs/ADMOB.md §12): проверить юристу.
const PARTNER_SITES_URL = 'https://policies.google.com/technologies/partner-sites';

const translations: Record<LegalLanguage, PolicyTranslations> = {
  en: {
    title: 'Privacy Policy',
    lastUpdated: `Last updated: ${getLastUpdatedDate('en')}`,
    effectiveDate: `Effective Date: ${getEffectiveDate('en')}`,
    dataController: getDataController('en'),
    intro:
      'This Privacy Policy explains how Qevryno ("the App") handles information. Qevryno is a single-player game. It does not require an account, and your progress is stored on your device. The game works offline; an internet connection is used only for purchases and for optional rewarded videos.',
    sections: [
      {
        title: '1. Summary',
        content:
          'We do not run a game server and we do not collect personal data ourselves: there are no accounts and no analytics or crash-reporting tools of our own. The App shows optional rewarded video ads from Google AdMob; Google’s software may process technical data as described in the Advertising section. Purchases are processed by Apple and Google.',
      },
      {
        title: '2. Data on Your Device',
        content:
          'The App stores gameplay data on your device: progress, inventory, settings, language, a local record of purchases already granted and local play statistics. This data is not sent to us.',
      },
      {
        title: '3. Backups and Moving Your Progress',
        content:
          'Your device may include the App’s save file in its own backup: on iOS in your iCloud or computer backup, on Android in your Google backup (only the save file). These backups are made by the operating system to your own account and are managed in your device settings; we have no access to them. In Settings → Backup you can also copy a save code or share a save file to move your progress to another device. You create and keep them yourself; they contain your game progress and are not sent to us.',
      },
      {
        title: '4. Reset and Deletion',
        content:
          'Resetting progress in Settings clears your game progress but keeps your gems, unopened gift boxes and the record of purchases already granted, so that a purchase is never granted twice. To delete everything the App stores on your device, uninstall the App. Copies in device backups are managed in your device or account settings. Save codes and files you made stay wherever you saved them.',
      },
      {
        title: '5. In-App Purchases',
        content:
          'The App offers optional in-app purchases: gem packs and ingredient packs. Payments are processed by the Apple App Store or Google Play. We do not receive or store your payment information. The App receives only what is needed to grant a purchase (such as the product and transaction identifiers) and keeps it on your device so that a purchase is not granted twice.',
      },
      {
        title: '6. Advertising',
        content: `The App offers optional rewarded video ads through Google AdMob. A video starts only after you tap a button with the ad icon. You can play without ever watching one, and a reward is given only for a video watched to the end. There are no banner, pop-up or app-open ads. To request, show and measure ads and to prevent fraud, Google’s software may collect and share your IP address (which can indicate approximate location), device and advertising identifiers (the Android advertising ID; on iOS, the advertising identifier only if you allow tracking), interactions with ads and with the App (such as video views) and technical diagnostics. The ad software may process some of this data when it starts, even if you do not watch a video. In the European Economic Area, the United Kingdom and Switzerland we ask for your consent through Google’s consent form before ads are requested; on iOS the system tracking prompt may appear. If you decline, you can still play; ads may then be less personalized or unavailable. How Google uses this data: ${PARTNER_SITES_URL}`,
      },
      {
        title: '7. Notifications',
        content:
          'With your permission, the App may schedule local reminders (for example, when mana is restored). They are created and stored on your device; we do not use push servers, and no notification data is sent to us. You can turn them off in the game settings or in your device settings.',
      },
      {
        title: '8. Support Emails',
        content:
          'If you email us, we use your email address and message only to answer you. We do not sell, rent or share them. You can ask us to delete your messages at any time.',
      },
      {
        title: '9. Third-Party Services',
        content: `The App uses: Apple App Store (StoreKit) and Google Play Billing for purchases; Google AdMob (Google Mobile Ads SDK) for optional rewarded video ads; and Google’s User Messaging Platform to ask for consent where required. How Google uses data from apps that use its services: ${PARTNER_SITES_URL}`,
      },
      {
        title: '10. Your Choices',
        content: `You can play without watching any ad. Where the law requires it, you can withdraw or change your ad consent at any time in Settings → Ad privacy. You can also reset or limit the advertising identifier in your device settings and, on iOS, turn off tracking for the App in Settings → Privacy & Security → Tracking. To delete data on your device, see section 4. To delete your support emails, write to ${getSupportEmail()}.`,
      },
      {
        title: '11. Children',
        content:
          'Qevryno is made for a general audience and is not directed at children under 13. We do not knowingly collect personal information from children. If you believe a child has given us personal information, contact us at the address below and we will delete it. Ads are provided by Google AdMob with the settings described in the Advertising section.',
      },
      {
        title: '12. Security',
        content:
          'We do not operate servers with player accounts. Gameplay data stays on your device and in your own device backups and is protected by your device and operating system. Data sent to Google for ads is encrypted in transit.',
      },
      {
        title: '13. Changes to this Privacy Policy',
        content:
          'We may update this Privacy Policy when the App, the law or the services we use change. The latest version is always on this page with the “Last updated” date shown above.',
      },
      {
        title: '14. Contact',
        content: `For questions about privacy or security, contact: ${getSupportEmail()} • ${getSecurityEmail()}.`,
      },
    ],
  },
  ru: {
    title: 'Политика конфиденциальности',
    lastUpdated: `Последнее обновление: ${getLastUpdatedDate('ru')}`,
    effectiveDate: `Дата вступления в силу: ${getEffectiveDate('ru')}`,
    dataController: getDataController('ru'),
    intro:
      'Этот документ описывает, как Qevryno («приложение») обращается с данными. Qevryno — одиночная игра. Аккаунт не нужен, прогресс хранится на вашем устройстве. Игра работает без сети; интернет нужен только для покупок и необязательных роликов за награду.',
    sections: [
      {
        title: '1. Коротко',
        content:
          'Своего игрового сервера у нас нет, и сами мы персональные данные не собираем: аккаунтов нет, своих инструментов аналитики и сбора сбоев нет. В приложении есть необязательные рекламные ролики за награду от Google AdMob; ПО Google может обрабатывать технические данные, как описано в разделе «Реклама». Покупки обрабатывают Apple и Google.',
      },
      {
        title: '2. Данные на устройстве',
        content:
          'Приложение хранит игровые данные на вашем устройстве: прогресс, инвентарь, настройки, язык, локальный список уже начисленных покупок и локальную статистику игры. Нам эти данные не передаются.',
      },
      {
        title: '3. Резервные копии и перенос прогресса',
        content:
          'Устройство может включать файл сохранения игры в свою резервную копию: на iOS — в копию iCloud или на компьютере, на Android — в резервную копию Google (только файл сохранения). Эти копии делает операционная система в ваш собственный аккаунт, управляются они в настройках устройства; у нас к ним доступа нет. В «Настройки → Резервная копия» можно также скопировать код сохранения или поделиться файлом сохранения, чтобы перенести прогресс на другое устройство. Их создаёте и храните вы сами; в них ваш игровой прогресс, нам они не отправляются.',
      },
      {
        title: '4. Сброс и удаление',
        content:
          'Сброс прогресса в настройках стирает игровой прогресс, но оставляет кристаллы, неоткрытые подарки и список уже начисленных покупок — чтобы покупка не начислилась дважды. Чтобы удалить всё, что приложение хранит на устройстве, удалите приложение. Копии в резервных копиях устройства удаляются в настройках устройства или аккаунта. Созданные вами коды и файлы сохранения остаются там, куда вы их сохранили.',
      },
      {
        title: '5. Встроенные покупки',
        content:
          'В приложении есть необязательные покупки: наборы кристаллов и наборы ингредиентов. Оплату обрабатывают Apple App Store или Google Play. Мы не получаем и не храним ваши платёжные данные. Приложение получает только то, что нужно для начисления покупки (например, идентификаторы товара и транзакции), и хранит это на устройстве, чтобы покупка не начислилась дважды.',
      },
      {
        title: '6. Реклама',
        content: `В приложении есть необязательные рекламные ролики за награду, их показывает Google AdMob. Ролик запускается только после нажатия на кнопку со значком рекламы. Играть можно, ни разу его не посмотрев, а награда выдаётся только за ролик, досмотренный до конца. Баннеров, всплывающей рекламы и рекламы при запуске нет. Чтобы запросить, показать и измерить рекламу и бороться с мошенничеством, ПО Google может собирать и передавать ваш IP-адрес (по нему можно оценить примерное местоположение), идентификаторы устройства и рекламный идентификатор (Android advertising ID; на iOS — рекламный идентификатор, только если вы разрешили отслеживание), данные о взаимодействии с рекламой и приложением (например, просмотры роликов) и технические диагностические данные. Часть этих данных рекламное ПО может обработать при запуске, даже если вы не смотрите ролики. В Европейской экономической зоне, Великобритании и Швейцарии перед запросом рекламы мы спрашиваем согласие через форму Google; на iOS может появиться системный запрос на отслеживание. Если вы откажетесь, играть можно по-прежнему; реклама тогда может быть менее персональной или недоступной. Как Google использует эти данные: ${PARTNER_SITES_URL}`,
      },
      {
        title: '7. Уведомления',
        content:
          'С вашего разрешения приложение может ставить локальные напоминания (например, о восстановлении маны). Они создаются и хранятся на устройстве; push-серверов у нас нет, данные уведомлений нам не отправляются. Отключить их можно в настройках игры или устройства.',
      },
      {
        title: '8. Письма в поддержку',
        content:
          'Если вы пишете нам, мы используем ваш адрес и письмо только для ответа. Мы не продаём, не сдаём в аренду и не передаём их. Вы можете в любой момент попросить удалить вашу переписку.',
      },
      {
        title: '9. Сторонние сервисы',
        content: `Приложение использует: Apple App Store (StoreKit) и Google Play Billing — для покупок; Google AdMob (Google Mobile Ads SDK) — для необязательных роликов за награду; Google User Messaging Platform — чтобы спросить согласие там, где это требуется. Как Google использует данные приложений, подключённых к его сервисам: ${PARTNER_SITES_URL}`,
      },
      {
        title: '10. Ваш выбор',
        content: `Играть можно, не смотря рекламу. Там, где этого требует закон, согласие на рекламу можно в любой момент отозвать или изменить в «Настройки → Конфиденциальность рекламы». Рекламный идентификатор можно сбросить или ограничить в настройках устройства, а на iOS — отключить отслеживание для приложения в «Настройки → Конфиденциальность и безопасность → Отслеживание». Как удалить данные на устройстве — раздел 4. Удалить переписку с поддержкой: ${getSupportEmail()}.`,
      },
      {
        title: '11. Дети',
        content:
          'Qevryno сделана для широкой аудитории и не предназначена для детей младше 13 лет. Мы сознательно не собираем персональные данные детей. Если вы считаете, что ребёнок передал нам персональные данные, напишите по адресу ниже — мы их удалим. Рекламу показывает Google AdMob с настройками из раздела «Реклама».',
      },
      {
        title: '12. Безопасность',
        content:
          'Серверов с аккаунтами игроков у нас нет. Игровые данные остаются на вашем устройстве и в ваших резервных копиях и защищены средствами устройства и операционной системы. Данные, которые уходят в Google для рекламы, шифруются при передаче.',
      },
      {
        title: '13. Изменения политики',
        content:
          'Мы можем обновлять эту политику, когда меняются приложение, закон или сервисы, которыми мы пользуемся. Актуальная версия всегда на этой странице, с датой «Последнее обновление» выше.',
      },
      {
        title: '14. Контакты',
        content: `По вопросам приватности и безопасности: ${getSupportEmail()} • ${getSecurityEmail()}.`,
      },
    ],
  },
  es: {
    title: 'Política de privacidad',
    lastUpdated: `Última actualización: ${getLastUpdatedDate('es')}`,
    effectiveDate: `Fecha de vigencia: ${getEffectiveDate('es')}`,
    dataController: getDataController('es'),
    intro:
      'Este documento explica cómo Qevryno («la App») trata la información. Qevryno es un juego individual. No requiere cuenta y su progreso se guarda en su dispositivo. El juego funciona sin conexión; internet se usa solo para las compras y los vídeos opcionales con recompensa.',
    sections: [
      {
        title: '1. Resumen',
        content:
          'No tenemos servidor del juego y no recopilamos datos personales por nuestra cuenta: no hay cuentas ni herramientas propias de analítica o de informes de fallos. La App muestra vídeos publicitarios opcionales con recompensa de Google AdMob; el software de Google puede tratar datos técnicos como se describe en la sección «Publicidad». Las compras las procesan Apple y Google.',
      },
      {
        title: '2. Datos en su dispositivo',
        content:
          'La App guarda datos del juego en su dispositivo: progreso, inventario, ajustes, idioma, un registro local de las compras ya concedidas y estadísticas locales de juego. Estos datos no se nos envían.',
      },
      {
        title: '3. Copias de seguridad y traslado del progreso',
        content:
          'Su dispositivo puede incluir el archivo de guardado de la App en su propia copia de seguridad: en iOS, en la copia de iCloud o del ordenador; en Android, en la copia de seguridad de Google (solo el archivo de guardado). Estas copias las hace el sistema operativo en su propia cuenta y se gestionan en los ajustes del dispositivo; no tenemos acceso a ellas. En Ajustes → Copia de seguridad también puede copiar un código de guardado o compartir un archivo de guardado para llevar su progreso a otro dispositivo. Usted los crea y los guarda; contienen su progreso en el juego y no se nos envían.',
      },
      {
        title: '4. Reinicio y eliminación',
        content:
          'Reiniciar el progreso en Ajustes borra el progreso del juego, pero conserva las gemas, los regalos sin abrir y el registro de compras ya concedidas, para que una compra nunca se conceda dos veces. Para borrar todo lo que la App guarda en su dispositivo, desinstale la App. Las copias en las copias de seguridad del dispositivo se gestionan en los ajustes del dispositivo o de la cuenta. Los códigos y archivos de guardado que haya creado siguen donde los guardó.',
      },
      {
        title: '5. Compras dentro de la app',
        content:
          'La App ofrece compras opcionales: paquetes de gemas y paquetes de ingredientes. Los pagos los procesan Apple App Store o Google Play. No recibimos ni almacenamos sus datos de pago. La App recibe solo lo necesario para conceder la compra (como los identificadores del producto y de la transacción) y lo guarda en el dispositivo para que una compra no se conceda dos veces.',
      },
      {
        title: '6. Publicidad',
        content: `La App ofrece vídeos publicitarios opcionales con recompensa a través de Google AdMob. Un vídeo solo empieza después de pulsar un botón con el icono de anuncio. Se puede jugar sin ver ninguno, y la recompensa se concede solo por un vídeo visto hasta el final. No hay banners, anuncios emergentes ni anuncios al abrir la App. Para solicitar, mostrar y medir anuncios y prevenir fraudes, el software de Google puede recopilar y compartir su dirección IP (que puede indicar una ubicación aproximada), identificadores del dispositivo y publicitario (el ID de publicidad de Android; en iOS, el identificador publicitario solo si permite el seguimiento), interacciones con los anuncios y con la App (como reproducciones de vídeo) y diagnósticos técnicos. El software publicitario puede tratar parte de estos datos al iniciarse, aunque no vea ningún vídeo. En el Espacio Económico Europeo, el Reino Unido y Suiza pedimos su consentimiento mediante el formulario de Google antes de solicitar anuncios; en iOS puede aparecer el aviso de seguimiento del sistema. Si lo rechaza, podrá seguir jugando; los anuncios pueden ser entonces menos personalizados o no estar disponibles. Cómo usa Google estos datos: ${PARTNER_SITES_URL}`,
      },
      {
        title: '7. Notificaciones',
        content:
          'Con su permiso, la App puede programar recordatorios locales (por ejemplo, cuando se restaura el maná). Se crean y guardan en su dispositivo; no usamos servidores push y no se nos envía ningún dato de notificaciones. Puede desactivarlas en los ajustes del juego o del dispositivo.',
      },
      {
        title: '8. Correos de soporte',
        content:
          'Si nos escribe, usamos su dirección y su mensaje solo para responderle. No los vendemos, alquilamos ni compartimos. Puede pedirnos que eliminemos sus mensajes en cualquier momento.',
      },
      {
        title: '9. Servicios de terceros',
        content: `La App utiliza: Apple App Store (StoreKit) y Google Play Billing para las compras; Google AdMob (Google Mobile Ads SDK) para los vídeos opcionales con recompensa; y la Plataforma de mensajes de usuario de Google para pedir el consentimiento donde sea necesario. Cómo usa Google los datos de las apps que emplean sus servicios: ${PARTNER_SITES_URL}`,
      },
      {
        title: '10. Sus opciones',
        content: `Puede jugar sin ver ningún anuncio. Donde la ley lo exige, puede retirar o cambiar su consentimiento publicitario en cualquier momento en Ajustes → Privacidad de anuncios. También puede restablecer o limitar el identificador publicitario en los ajustes del dispositivo y, en iOS, desactivar el seguimiento de la App en Ajustes → Privacidad y seguridad → Seguimiento. Para borrar los datos del dispositivo, consulte la sección 4. Para eliminar sus correos de soporte, escriba a ${getSupportEmail()}.`,
      },
      {
        title: '11. Niños',
        content:
          'Qevryno está pensada para un público general y no está dirigida a menores de 13 años. No recopilamos conscientemente información personal de niños. Si cree que un menor nos ha facilitado datos personales, escríbanos a la dirección de abajo y los eliminaremos. La publicidad la ofrece Google AdMob con los ajustes descritos en la sección «Publicidad».',
      },
      {
        title: '12. Seguridad',
        content:
          'No tenemos servidores con cuentas de jugadores. Los datos del juego permanecen en su dispositivo y en sus propias copias de seguridad y están protegidos por el dispositivo y el sistema operativo. Los datos que se envían a Google para la publicidad se cifran durante la transmisión.',
      },
      {
        title: '13. Cambios en esta política',
        content:
          'Podemos actualizar esta política cuando cambien la App, la ley o los servicios que usamos. La versión vigente está siempre en esta página, con la fecha de «Última actualización» indicada arriba.',
      },
      {
        title: '14. Contacto',
        content: `Preguntas sobre privacidad o seguridad: ${getSupportEmail()} • ${getSecurityEmail()}.`,
      },
    ],
  },
};

export const Privacy: React.FC = () => {
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const [currentLang, setCurrentLang] = useState<Language>(isLanguage(lang) ? lang : 'ru');

  useEffect(() => {
    if (isLanguage(lang)) {
      setCurrentLang(lang);
    }
  }, [lang]);

  const docLang = legalLanguage(currentLang);
  const t = translations[docLang];
  const onlyIn = docLang === currentLang ? '' : STRINGS[currentLang].legal.onlyIn;

  return (
    <div
      className="landing"
      style={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at 15% 20%, var(--color-rgba-59-130-246-16), var(--color-transparent) 35%), radial-gradient(circle at 85% 10%, var(--color-rgba-124-58-237-12), var(--color-transparent) 30%), var(--background)',
        color: 'var(--text)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
      }}
    >
      <Header
        language={currentLang}
        onLanguageChange={(newLang) => {
          setCurrentLang(newLang);
          navigate(`/privacy/${newLang}`);
        }}
      />

      <div
        style={{
          flex: 1,
          padding: 'clamp(2.5rem, 5vw, 4rem) clamp(1.4rem, 4vw, 3rem) clamp(3rem, 5vw, 5rem)',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, var(--color-rgba-59-130-246-8), var(--color-rgba-17-24-39-35))',
            opacity: 0.9,
            pointerEvents: 'none',
          }}
        />
        <div style={{ maxWidth: '56rem', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <h1
            style={{
              fontSize: 'var(--font-h1)',
              margin: '0 0 0.5rem',
              lineHeight: 1.1,
              background: 'linear-gradient(120deg, var(--text) 0%, var(--primary) 70%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'var(--color-transparent)',
              backgroundClip: 'text',
            }}
          >
            {t.title}
          </h1>
          {onlyIn && (
            <p lang={currentLang} style={{ margin: '0 0 1rem', fontSize: 'var(--text-small)', color: 'var(--textSecondary)' }}>{onlyIn}</p>
          )}
          <div style={{ margin: '0 0 2rem' }}>
            <p style={{ margin: '0 0 0.25rem', fontSize: 'var(--text-small)', color: 'var(--textTertiary)' }}>
              {t.lastUpdated}
            </p>
            <p style={{ margin: '0 0 0.25rem', fontSize: 'var(--text-small)', color: 'var(--textTertiary)' }}>
              {t.effectiveDate}
            </p>
            {t.dataController && (
              <p
                style={{
                  margin: '0 0 0.5rem',
                  fontSize: 'var(--text-small)',
                  color: 'var(--textSecondary)',
                  fontWeight: 600,
                }}
              >
                {t.dataController}
              </p>
            )}
            <p
              style={{
                margin: '1rem 0 0',
                padding: '1rem',
                fontSize: 'var(--text-body)',
                color: 'var(--textSecondary)',
                background: 'var(--color-rgba-59-130-246-10)',
                borderLeft: '3px solid var(--primary)',
                borderRadius: '0.5rem',
                lineHeight: 1.6,
                fontStyle: 'italic',
              }}
            >
              {t.intro}
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            {t.sections.map((section, index) => (
              <div
                key={index}
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '1.2rem',
                  padding: '1.5rem',
                }}
              >
                <h2
                  style={{
                    fontSize: 'var(--font-h2)',
                    fontWeight: 700,
                    margin: '0 0 0.75rem',
                    color: 'var(--text)',
                  }}
                >
                  {section.title}
                </h2>
                <p
                  style={{
                    margin: 0,
                    fontSize: 'var(--text-body)',
                    color: 'var(--textSecondary)',
                    lineHeight: 1.6,
                  }}
                >
                  {section.content}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer language={currentLang} />
    </div>
  );
};
